// Centralised JSON-LD structured data for SEO + AI search compatibility.
// Plain JS objects; injected via the useSeo() hook.

import { SITE_URL } from "@/lib/seo";

export const ORG_LD = {
  "@context": "https://schema.org",
  "@type": ["Organization", "LocalBusiness"],
  "@id": `${SITE_URL}#organization`,
  name: "Asia Sourcing",
  alternateName: "Asia Sourcing India",
  slogan: "India sourcing partner for international retailers, importers and brands.",
  description:
    "Asia Sourcing is an India-based sourcing, supplier verification, factory audit, quality control and product development partner serving international retailers, importers and brands across the United States, United Kingdom, Europe and the Middle East. Headquartered in Moradabad; sourcing across 15 manufacturing clusters (8 with permanent on-ground team presence) and six categories — Lighting, Furniture, Home décor, Textile, Brass & metalware, and Fashion accessories.",
  url: SITE_URL,
  logo: `${SITE_URL}/assets/logo.svg`,
  image: `${SITE_URL}/assets/logo.jpg`,
  foundingDate: "2002",
  founder: {
    "@type": "Person",
    name: "Dr. Sumit Chhabra",
    jobTitle: "Founder & CEO",
    alumniOf: [
      { "@type": "CollegeOrUniversity", name: "Symbiosis International University" },
      { "@type": "CollegeOrUniversity", name: "Savitribai Phule Pune University" },
    ],
    sameAs: ["https://www.linkedin.com/in/dr-sumit-chhabra-158a1b28"],
  },
  address: {
    "@type": "PostalAddress",
    streetAddress: "D-8 Manarover Colony, Delhi Road",
    addressLocality: "Moradabad",
    addressRegion: "Uttar Pradesh",
    postalCode: "244001",
    addressCountry: "IN",
  },
  geo: {
    "@type": "GeoCoordinates",
    latitude: 28.8386,
    longitude: 78.7733,
  },
  contactPoint: [
    {
      "@type": "ContactPoint",
      contactType: "sales",
      email: "Hello@asiasourcing.co.in",
      telephone: "+91-90123-02222",
      areaServed: ["US", "GB", "EU", "AU", "CA", "Worldwide"],
      availableLanguage: ["English"],
    },
  ],
  telephone: "+91-90123-02222",
  email: "Hello@asiasourcing.co.in",
  knowsAbout: [
    "India sourcing",
    "India sourcing agent",
    "Buying agent India",
    "Supplier verification India",
    "Factory audit India",
    "Quality control India",
    "Product development India",
    "AQL inspection",
    "Pre-shipment inspection",
    "Social compliance",
    "BSCI",
    "Sedex",
    "ISO 9001",
    "FSC",
    "GOTS",
    "Container loading supervision",
    "Lighting",
    "Furniture",
    "Home décor",
    "Textile",
    "Brass & metalware",
    "Fashion accessories",
  ],
  areaServed: [
    { "@type": "Country", name: "United States" },
    { "@type": "Country", name: "United Kingdom" },
    { "@type": "Place", name: "Europe" },
    { "@type": "Place", name: "Middle East" },
  ],
  // Only verified official profiles. Do not add hallucinated/unverified URLs.
  sameAs: [
    "https://www.linkedin.com/in/dr-sumit-chhabra-158a1b28",
  ],
};

export const SERVICES_LD = {
  "@context": "https://schema.org",
  "@type": "ItemList",
  name: "Asia Sourcing — Services",
  itemListElement: [
    serviceItem(1, "Product Development", "Drawings, tech-packs, costed samples and golden-sample retention across lighting, furniture and home décor."),
    serviceItem(2, "Sourcing", "Three-to-five vetted factory shortlist per brief across eight Indian clusters. MOQ negotiation from 50 units."),
    serviceItem(3, "Quality Control & Inspection", "AQL 2.5 inspection, in-line QC, pre-shipment audits and photo-documented reports within 48 hours."),
    serviceItem(4, "Logistics & Export", "Delhi NCR consolidation hub, CHA-handled export documentation, six active export ports."),
  ],
};

function serviceItem(position, name, description) {
  return {
    "@type": "ListItem",
    position,
    item: {
      "@type": "Service",
      name,
      description,
      provider: { "@id": `${SITE_URL}#organization` },
      areaServed: { "@type": "Country", name: "India" },
    },
  };
}

export const TESTING_CATEGORIES = [
  { id: "aql-inspection", name: "AQL Inspection", body: "AQL 2.5 / 4.0 sampling, defect classification, accept/reject decisions logged per ISO 2859-1." },
  { id: "pre-shipment", name: "Pre-shipment Inspection (PSI)", body: "Final random inspection on packed goods before container loading. Photo evidence within 48 hours." },
  { id: "during-production", name: "During-production Inspection (DPI)", body: "In-line checks once 20–60% of the order is completed — catches drift early, not after." },
  { id: "initial-production", name: "Initial Production Check (IPC)", body: "First-off-the-line verification against golden sample. Materials, dimensions, finish, hardware." },
  { id: "container-loading", name: "Container Loading Supervision", body: "Photographed seal, cube utilisation, dunnage and load pattern. One bill of lading, one ship date." },
  { id: "factory-audit", name: "Factory Capability Audit", body: "Capacity, machinery, technical capability and financial health verified on site before any PO." },
  { id: "social-compliance", name: "Social Compliance Audit", body: "BSCI · Sedex · SA8000 · WRAP — labour, human rights, workplace safety and environmental standards." },
  { id: "supply-chain-security", name: "Supply Chain Security Audit", body: "C-TPAT/AEO-aligned checks: access control, conveyance, personnel, IT and inbound logistics." },
  { id: "sustainability", name: "Sustainability & Environment", body: "FSC chain-of-custody for wood, GOTS / GRS for textile, environmental compliance per ISO 14001." },
  { id: "material-testing", name: "Material & Lab Testing", body: "Brass alloy composition, wood moisture, glass thermal shock, ceramic lead/cadmium, textile colour-fastness." },
  { id: "drop-test", name: "Drop / Breakage Test (ISTA-3A)", body: "Carton drop testing for fragile categories — blue pottery, hand-blown glass — to ISTA-3A standard." },
  { id: "photometry", name: "Photometric Test (Lighting)", body: "Lumen output, colour temperature, CRI, IP rating and CE / UL safety verification for lighting SKUs." },
  { id: "salt-spray", name: "Corrosion & Salt-spray", body: "Coating durability for brass and iron — neutral salt-spray (ASTM B117) per agreed cycle hours." },
  { id: "vendor-monitoring", name: "Vendor Monitoring", body: "Quarterly factory review — capacity, on-time delivery, defect-rate trending. Reported to the buyer." },
];

export const TESTING_LD = {
  "@context": "https://schema.org",
  "@type": "ItemList",
  name: "Asia Sourcing — Testing & Inspection categories",
  itemListElement: TESTING_CATEGORIES.map((t, i) => ({
    "@type": "ListItem",
    position: i + 1,
    item: { "@type": "Service", name: t.name, description: t.body, provider: { "@id": `${SITE_URL}#organization` } },
  })),
};

export const FAQ_LD = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    faq(
      "Where is Asia Sourcing based?",
      "Asia Sourcing is headquartered in Moradabad, Uttar Pradesh — India's Brass City — at D-8 Manarover Colony, Delhi Road, Moradabad 244001. The studio was founded there in 2002 by Dr. Sumit Chhabra."
    ),
    faq(
      "What product categories does Asia Sourcing handle?",
      "Six categories — Lighting, Furniture, Home décor, Textile, Brass & metalware, and Fashion accessories — sourced across fifteen manufacturing clusters including brass and metalware (Moradabad), hand-blown glass (Firozabad), blue pottery (Jaipur), carved and metal furniture (Jodhpur), wood carving (Saharanpur), textile (Panipat, Karur), hand-knotted rugs (Bhadohi), silk (Varanasi), marble inlay (Agra), pashmina and paper mache (Kashmir), and mixed décor (Noida, Delhi, Gurugram)."
    ),
    faq(
      "What inspection and testing categories does Asia Sourcing perform?",
      "AQL 2.5 inspection, pre-shipment inspection (PSI), during-production inspection (DPI), initial production check (IPC), container-loading supervision, factory capability audits, social compliance audits (BSCI · Sedex · SA8000 · WRAP), supply-chain security, sustainability (FSC · GOTS · GRS), material and lab testing, ISTA-3A drop/breakage testing, photometric testing for lighting and salt-spray corrosion testing."
    ),
    faq(
      "Which certifications does Asia Sourcing operate to?",
      "Our standard stack is BSCI · Sedex · ISO 9001 · FSC · GOTS. We can run additional schemes (SA8000, WRAP, GRS, Higg) on request and have C-TPAT/AEO-aligned supply chain security workflows."
    ),
    faq(
      "How long does product development take?",
      "Costed samples are typically delivered within three to six weeks. Floor pricing per SKU is agreed at sample sign-off; golden samples are retained on site at the factory for the life of the program."
    ),
    faq(
      "What is the minimum order quantity (MOQ)?",
      "MOQs vary by cluster: furniture (Jodhpur) from 50 units, blue pottery (Jaipur) from 300, mixed décor (Noida) from 300, brass (Moradabad) from 500, textile (Panipat) from 500, glass (Firozabad) from 1,000."
    ),
    faq(
      "Which ports can Asia Sourcing ship from?",
      "Six active export ports — Nhava Sheva (JNPT), Mundra, Chennai, Kolkata, plus Hamburg- and Los Angeles-bound consolidation. Consolidation is handled from our Delhi NCR hub with CHA-managed export documentation."
    ),
    faq(
      "Who founded Asia Sourcing?",
      "Asia Sourcing was founded in 2002 by Dr. Sumit Chhabra, who has 23+ years of factory-floor experience and has personally visited more than 1,000 factories across India. He holds degrees from Symbiosis International University and Savitribai Phule Pune University."
    ),
  ],
};

function faq(q, a) {
  return {
    "@type": "Question",
    name: q,
    acceptedAnswer: { "@type": "Answer", text: a },
  };
}

export function breadcrumb(items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => {
      // Accept { name, path|item } objects AND legacy [name, urlOrPath]
      // tuples — the tuple form previously dropped `name` entirely, which
      // failed Google breadcrumb validation ("Either 'name' or 'item.name'
      // should be specified").
      const name = Array.isArray(it) ? it[0] : it.name;
      const raw = Array.isArray(it)
        ? it[1]
        : it.item != null
          ? it.item
          : it.path;
      const item =
        raw == null ? SITE_URL : raw.startsWith("http") ? raw : SITE_URL + raw;
      return { "@type": "ListItem", position: i + 1, name, item };
    }),
  };
}

export const WEBSITE_LD = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  url: SITE_URL,
  name: "Asia Sourcing",
  publisher: { "@id": `${SITE_URL}#organization` },
  inLanguage: "en",
};
